/**
 * Converte texto com formatação do WhatsApp para HTML seguro.
 * Suporta: *negrito*, _itálico_, ~tachado~, `mono`, ```bloco```, quebras de linha e links.
 */
export function escapeHtml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/** Normaliza quebras de linha vindas do WhatsApp, DB ou JSON. */
export function normalizeMessageLineBreaks(text) {
  return String(text ?? '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\\n/g, '\n')
}

/** Remove caracteres invisíveis que impedem o WhatsApp de reconhecer URLs. */
export function sanitizeWhatsAppOutboundText(text) {
  return normalizeMessageLineBreaks(text)
    .replace(/[\u200B-\u200D\uFEFF\u00AD\u2060]/g, '')
    .replace(/\u2028|\u2029/g, '\n')
}

const HTTP_URL_IN_TEXT_RE = /https?:\/\/[^\s<>"')\]]+/gi

/** Primeira URL http(s) no texto, sem pontuação final colada. */
export function extractFirstHttpUrl(text) {
  const body = sanitizeWhatsAppOutboundText(text)
  const match = body.match(HTTP_URL_IN_TEXT_RE)
  if (!match?.[0]) return null
  return match[0].replace(/[.,;:!?)}\]'"]+$/g, '')
}

/** Garante protocolo em domínios colados sem http(s) (ex.: www.site.com/path). */
export function ensureHttpUrlsInText(text) {
  const body = sanitizeWhatsAppOutboundText(text)
  return body.replace(
    /(^|[\s\n(])((?:www\.)?[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z]{2,})+(?:\/[^\s]*)?)/gi,
    (full, prefix, url) => {
      if (/^https?:\/\//i.test(url)) return full
      if (url.includes('@')) return full
      return `${prefix}https://${url}`
    }
  )
}

/** Texto pronto para envio ao gateway (links detectáveis pelo WhatsApp do cliente). */
export function prepareWhatsAppOutboundText(text) {
  return ensureHttpUrlsInText(text)
}

const URL_REGEX = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]'"])/gi

function wrapLinks(html, linkClass) {
  const cls = linkClass ? ` class="${linkClass}"` : ''
  return html.replace(
    URL_REGEX,
    `<a href="$1" target="_blank" rel="noopener noreferrer"${cls}>$1</a>`
  )
}

/**
 * @param {string} text
 * @param {{ linkClass?: string }} [options]
 * @returns {string}
 */
export function formatWhatsAppMessageHtml(text, options = {}) {
  if (text == null || text === '') return ''

  const linkClass = options.linkClass || 'whatsapp-message-link'

  let html = escapeHtml(normalizeMessageLineBreaks(text))

  // Blocos de código (multilinha)
  html = html.replace(/```([\s\S]*?)```/g, (_, code) => {
    const inner = code.replace(/\n/g, '<br>')
    return `<code class="whatsapp-code-block">${inner}</code>`
  })

  // Código inline
  html = html.replace(/`([^`\n]+)`/g, '<code class="whatsapp-inline-code">$1</code>')

  // Negrito *texto* (WhatsApp usa asterisco simples)
  html = html.replace(/\*([^\s*][^*\n]*?)\*/g, '<strong>$1</strong>')

  // Itálico _texto_
  html = html.replace(/_([^\s_][^_\n]*?)_/g, '<em>$1</em>')

  // Tachado ~texto~
  html = html.replace(/~([^\s~][^~\n]*?)~/g, '<s>$1</s>')

  // Quebras de linha: exibidas via white-space: pre-line em .whatsapp-message-body

  html = wrapLinks(html, linkClass)

  return html
}
